// src/modules/hr/pages/Approvals.tsx
'use client';

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, InputAdornment, IconButton, Button,
  TablePagination,
  Alert, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Stack, Grid, alpha, useTheme, useMediaQuery,
  Skeleton, ToggleButton, ToggleButtonGroup, Slide
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LaunchIcon from '@mui/icons-material/Launch';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FlightIcon from '@mui/icons-material/Flight';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { approvalsApi, type ApprovalListItem } from '@/api/hr';
import { useNavigate } from 'react-router-dom';

// Transition component for mobile
const Transition = React.forwardRef(function Transition(
  props: any,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Row = {
  id: number | string;     // display id (task id is fine)
  taskId: number;          // numeric for approve/reject
  entity: string;          // e.g. 'LEAVE_REQUEST'
  title: string;           // task title
  submittedByText: string; // friendly name
  submittedAt: string;     // ISO
  href?: string;           // route to entity
  step?: number;           // current step
};

const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString() : '—');
const parseErr = (e: any) =>
  e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Request failed.';

function submittedByToText(createdBy?: {
  id: number;
  username?: string | null;
  profile?: { imageUrl?: string | null; name?: string } | null;
} | null): string {
  if (!createdBy) return '—';
  return createdBy.profile?.name || createdBy.username || String(createdBy.id);
}

// Get entity icon and color
function getEntityInfo(entity: string) {
  const type = String(entity || '').toUpperCase();
  switch (type) {
    case 'LEAVE_REQUEST':
      return { icon: EventAvailableIcon, color: 'primary', label: 'Leave Request' };
    case 'LOAN_REQUEST':
      return { icon: AccountBalanceIcon, color: 'secondary', label: 'Loan Request' };
    case 'TRAVEL_REQUEST':
      return { icon: FlightIcon, color: 'info', label: 'Travel Request' };
    case 'EXPENSE_ADVANCE':
      return { icon: MonetizationOnIcon, color: 'success', label: 'Expense Advance' };
    default:
      return { icon: AssignmentIcon, color: 'default', label: 'Task' };
  }
}

// Get entity route using actual entity ID
function getEntityRoute(task: any): string | undefined {
  if (!task) return undefined;
  const type = String(task.type || '').toUpperCase();
  
  // Get the actual entity ID from the enriched task data
  let entityId: number | undefined;
  
  switch (type) {
    case 'LEAVE_REQUEST':
      entityId = (task as any).entityIds?.leaveRequestId;
      return entityId ? `/hr/leaves/${entityId}` : `/tasks/${task.id}`;
    case 'LOAN_REQUEST':
      entityId = (task as any).entityIds?.loanRequestId;
      return entityId ? `/hr/loans/${entityId}` : `/tasks/${task.id}`;
    case 'TRAVEL_REQUEST':
      entityId = (task as any).entityIds?.travelRequestId;
      return entityId ? `/hr/travels/${entityId}` : `/tasks/${task.id}`;
    case 'EXPENSE_ADVANCE':
      entityId = (task as any).entityIds?.expenseAdvanceId;
      return entityId ? `/hr/expense-advances/${entityId}` : `/tasks/${task.id}`;
    default:
      return `/tasks/${task.id}`;
  }
}

interface ApprovalsDialogProps {
  open: boolean;
  onClose: () => void;
}

// Main dialog component
function ApprovalsDialog({ open, onClose }: ApprovalsDialogProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Load dialog position from localStorage or default to 'center'
  const [dialogPosition, setDialogPosition] = useState<'right' | 'center' | 'full'>(() => {
    try {
      const saved = localStorage.getItem('approval_dialog_position');
      if (saved && ['right', 'center', 'full'].includes(saved)) {
        return saved as 'right' | 'center' | 'full';
      }
    } catch (error) {
      console.warn('Failed to load dialog position from localStorage:', error);
    }
    return 'center';
  });

  const [search, setSearch] = useState('');

  // Save dialog position to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('approval_dialog_position', dialogPosition);
    } catch (error) {
      console.warn('Failed to save dialog position to localStorage:', error);
    }
  }, [dialogPosition]);

  // client-side pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // approve/reject dialog
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgAction, setDlgAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [dlgNote, setDlgNote] = useState('');
  const [actingId, setActingId] = useState<number | string | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await approvalsApi.listMy({
        status: 'PENDING',
        currentStepOnly: true,
        limit: 100,
      });

      const normalized: Row[] = (data.items as ApprovalListItem[]).map((it) => {
        const task = it.task;

        // Debug logging
        console.log('Processing approval item:', {
          taskId: task?.id,
          taskType: task?.type,
          entityIds: (task as any)?.entityIds,
        });

        // Safely pick a submittedAt: prefer createdAt, then startDate, then actedAt, then now
        const submittedAt =
          (task && (task as any)?.createdAt) ||
          (task && (task as any)?.startDate) ||
          it.actedAt ||
          new Date().toISOString();

        const href = getEntityRoute(task);
        console.log('Generated href:', href);

        return {
          id: task?.id ?? it.taskId ?? it.id,
          taskId: Number(task?.id ?? it.taskId ?? it.id), // ensure number
          entity: String(task?.type || 'TASK'),
          title: task?.title || '(untitled task)',
          submittedByText: submittedByToText(task?.createdBy ?? null),
          submittedAt: String(submittedAt),
          href,
          step: task?.currentApprovalStep ?? undefined,
        };
      });

      setRows(normalized);
    } catch (e: any) {
      setErr(parseErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { 
    if (open) {
      void load(); 
    }
  }, [open, load]);

  // search filter (client-side)
  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.title?.toLowerCase().includes(q) ||
      r.entity?.toLowerCase().includes(q) ||
      r.submittedByText.toLowerCase().includes(q)
    );
  }, [rows, search]);

  // current page slice
  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  // open dialog
  const openAct = (taskIdLike: number | string, action: 'APPROVE' | 'REJECT') => {
    setActingId(taskIdLike);
    setDlgAction(action);
    setDlgNote('');
    setDlgOpen(true);
  };

  const doAct = async () => {
    if (actingId == null) return;

    // Convert to number for /approvals/:taskId endpoints
    const taskId = Number(actingId);
    if (!Number.isFinite(taskId) || taskId <= 0) {
      setErr('Invalid task id.');
      return;
    }

    setActing(true);
    setErr(null);
    try {
      if (dlgAction === 'APPROVE') {
        await approvalsApi.approve(taskId, dlgNote || undefined);
      } else {
        await approvalsApi.reject(taskId, dlgNote || undefined);
      }
      setDlgOpen(false);
      setActingId(null);
      await load(); // Refresh
    } catch (e) {
      setErr(parseErr(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={dialogPosition === 'full' ? false : dialogPosition === 'right' ? 'sm' : 'lg'}
      fullWidth={dialogPosition !== 'full'}
      fullScreen={isMobile || dialogPosition === 'full'}
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile || dialogPosition === 'full' ? 0 : 2,
          height: isMobile || dialogPosition === 'full' ? '100vh' : '85vh',
          maxHeight: isMobile || dialogPosition === 'full' ? '100vh' : '85vh',
          margin: isMobile || dialogPosition === 'full' ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          ...(dialogPosition === 'right' && {
            position: 'fixed',
            right: 0,
            top: 0,
            bottom: 0,
            width: '400px',
            maxWidth: '400px',
            height: '100vh',
            maxHeight: '100vh',
            margin: 0,
            borderRadius: 0,
          }),
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}10 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography 
            variant="h6" 
            fontWeight={700}
            sx={{ color: theme.palette.text.primary }}
          >
            Pending Approvals
          </Typography>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            {filtered.length} Items
          </Box>
        </Box>
        
        {/* Dialog Position Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={dialogPosition}
            exclusive
            onChange={(_, value) => value && setDialogPosition(value)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1.5,
                py: 0.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                borderRadius: 1,
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                  },
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.action.hover, 0.5),
                },
              },
            }}
          >
            <ToggleButton value="right" aria-label="Right panel">
              <Tooltip title="Right Panel" arrow>
                <ViewSidebarIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="center" aria-label="Center dialog">
              <Tooltip title="Center Dialog" arrow>
                <CenterFocusStrongIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="full" aria-label="Full page">
              <Tooltip title="Full Page" arrow>
                <FullscreenIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 3, flex: 1, overflow: 'auto', bgcolor: "background.default" }}>
          {/* Search and Filter Section */}
          <Card elevation={0} sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
            <CardContent sx={{ p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search by title, entity, or submitter…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                  sx={{ 
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {search && (
                          <IconButton
                            size="small"
                            onClick={() => { setSearch(""); setPage(0); }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={loading}
                  sx={{ borderRadius: 2 }}
                >
                  Refresh
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          {/* Approvals List */}
          <Grid container spacing={dialogPosition === 'right' ? 1.5 : 2}>
            {loading ? (
              // Loading skeleton
              [...Array(3)].map((_, index) => (
                <Grid item xs={12} key={index}>
                  <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Skeleton variant="circular" width={48} height={48} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="60%" height={24} />
                          <Skeleton variant="text" width="40%" height={20} />
                        </Box>
                        <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : paged.length === 0 ? (
              <Grid item xs={12}>
                <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                  <CardContent sx={{ p: 4, textAlign: "center" }}>
                    <AssignmentIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No pending approvals
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search ? "Try adjusting your search criteria" : "All caught up! No approvals are waiting for your review."}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              paged.map((r) => {
                const entityInfo = getEntityInfo(r.entity);
                const EntityIcon = entityInfo.icon;
                
                return (
                  <Grid item xs={12} key={`${r.taskId}-${r.id}`}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        border: `1px solid ${theme.palette.divider}`,
                        transition: 'all 0.2s ease',
                        ...(dialogPosition === 'right' && {
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
                            transform: 'translateY(-1px)',
                          }
                        }),
                        ...(dialogPosition !== 'right' && {
                          '&:hover': {
                            boxShadow: theme.shadows[4],
                            transform: 'translateY(-2px)',
                          }
                        })
                      }}
                    >
                      <CardContent sx={{ 
                        p: dialogPosition === 'right' ? 2 : 3,
                        '&:last-child': { pb: dialogPosition === 'right' ? 2 : 3 }
                      }}>
                        <Stack 
                          direction={dialogPosition === 'right' ? "column" : { xs: "column", sm: "row" }} 
                          spacing={dialogPosition === 'right' ? 1.5 : 2} 
                          alignItems={dialogPosition === 'right' ? "stretch" : "flex-start"}
                        >
                          {/* Entity Icon and Info */}
                          <Box 
                            display="flex" 
                            alignItems={dialogPosition === 'right' ? "flex-start" : "center"} 
                            gap={dialogPosition === 'right' ? 1.5 : 2} 
                            sx={{ minWidth: 0, flex: 1 }}
                          >
                            <Box
                              sx={{
                                p: dialogPosition === 'right' ? 1 : 1.5,
                                borderRadius: 2,
                                backgroundColor: alpha(
                                  entityInfo.color === 'primary' ? theme.palette.primary.main :
                                  entityInfo.color === 'secondary' ? theme.palette.secondary.main :
                                  entityInfo.color === 'info' ? theme.palette.info.main :
                                  entityInfo.color === 'success' ? theme.palette.success.main :
                                  theme.palette.grey[500], 0.1),
                                color: entityInfo.color === 'primary' ? theme.palette.primary.main :
                                       entityInfo.color === 'secondary' ? theme.palette.secondary.main :
                                       entityInfo.color === 'info' ? theme.palette.info.main :
                                       entityInfo.color === 'success' ? theme.palette.success.main :
                                       theme.palette.grey[500],
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <EntityIcon sx={{ fontSize: dialogPosition === 'right' ? 16 : 20 }} />
                            </Box>
                            
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Stack 
                                direction={dialogPosition === 'right' ? "column" : "row"} 
                                spacing={1} 
                                alignItems={dialogPosition === 'right' ? "flex-start" : "center"} 
                                mb={dialogPosition === 'right' ? 0.5 : 1}
                              >
                                <Typography 
                                  variant={dialogPosition === 'right' ? "body1" : "h6"} 
                                  fontWeight={600} 
                                  noWrap
                                  sx={{
                                    fontSize: dialogPosition === 'right' ? '0.875rem' : undefined,
                                    lineHeight: dialogPosition === 'right' ? 1.3 : undefined,
                                  }}
                                >
                                  {r.title}
                                </Typography>
                                <Chip 
                                  size="small" 
                                  label={entityInfo.label}
                                  color={entityInfo.color as any}
                                  variant="outlined"
                                  sx={{
                                    fontSize: dialogPosition === 'right' ? '0.65rem' : undefined,
                                    height: dialogPosition === 'right' ? 20 : undefined,
                                  }}
                                />
                              </Stack>
                              
                              <Stack 
                                direction={dialogPosition === 'right' ? "column" : "row"} 
                                spacing={dialogPosition === 'right' ? 0.5 : 2} 
                                alignItems={dialogPosition === 'right' ? "flex-start" : "center"} 
                                flexWrap={dialogPosition === 'right' ? "nowrap" : "wrap"}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <PersonIcon fontSize="small" color="action" />
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{
                                      fontSize: dialogPosition === 'right' ? '0.75rem' : undefined,
                                    }}
                                  >
                                    {r.submittedByText}
                                  </Typography>
                                </Box>
                                
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{
                                    fontSize: dialogPosition === 'right' ? '0.75rem' : undefined,
                                  }}
                                >
                                  {fmtDateTime(r.submittedAt)}
                                </Typography>
                                
                                {typeof r.step === 'number' && (
                                  <Chip 
                                    size="small" 
                                    label={`Step ${r.step}`} 
                                    color="primary"
                                    variant="outlined"
                                    sx={{
                                      fontSize: dialogPosition === 'right' ? '0.65rem' : undefined,
                                      height: dialogPosition === 'right' ? 20 : undefined,
                                    }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          </Box>

                          {/* Actions */}
                          <Stack 
                            direction={dialogPosition === 'right' ? "column" : "row"} 
                            spacing={1} 
                            alignItems={dialogPosition === 'right' ? "stretch" : "center"}
                            sx={{
                              minWidth: dialogPosition === 'right' ? 120 : 'auto',
                              ...(dialogPosition === 'right' && {
                                '& .MuiButton-root': {
                                  minWidth: '100%',
                                  justifyContent: 'flex-start',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                }
                              })
                            }}
                          >
                            {r.href ? (
                              <Tooltip title="View Details">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<LaunchIcon />}
                                  onClick={() => {
                                    console.log('Navigating to:', r.href);
                                    if (r.href) {
                                      navigate(r.href);
                                    }
                                  }}
                                  sx={{ 
                                    borderRadius: 2,
                                    ...(dialogPosition === 'right' && {
                                      borderColor: alpha(theme.palette.info.main, 0.3),
                                      color: theme.palette.info.main,
                                      '&:hover': {
                                        borderColor: theme.palette.info.main,
                                        bgcolor: alpha(theme.palette.info.main, 0.08),
                                      }
                                    })
                                  }}
                                >
                                  Open
                                </Button>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Entity not found">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                  sx={{ borderRadius: 2 }}
                                >
                                  No Link
                                </Button>
                              </Tooltip>
                            )}
                            
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => openAct(r.taskId, 'APPROVE')}
                              sx={{ 
                                borderRadius: 2,
                                ...(dialogPosition === 'right' && {
                                  borderColor: alpha(theme.palette.success.main, 0.3),
                                  color: theme.palette.success.main,
                                  '&:hover': {
                                    borderColor: theme.palette.success.main,
                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                  }
                                })
                              }}
                            >
                              Approve
                            </Button>
                            
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => openAct(r.taskId, 'REJECT')}
                              sx={{ 
                                borderRadius: 2,
                                ...(dialogPosition === 'right' && {
                                  borderColor: alpha(theme.palette.error.main, 0.3),
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    borderColor: theme.palette.error.main,
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                  }
                                })
                              }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })
            )}
          </Grid>

          {/* Pagination */}
          {filtered.length > 0 && (
            <Card elevation={0} sx={{ mt: 2, border: `1px solid ${theme.palette.divider}` }}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_e, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    paddingLeft: 2,
                    paddingRight: 2,
                  }
                }}
              />
            </Card>
          )}
        </Box>
      </DialogContent>

      {/* Approve/Reject dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dlgAction === 'APPROVE' ? 'Approve' : 'Reject'} request</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You can leave a note (optional).
          </Typography>
          <TextField
            label="Note (optional)"
            multiline minRows={3} fullWidth
            value={dlgNote}
            onChange={(e)=>setDlgNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDlgOpen(false)}>Cancel</Button>
          <Button onClick={doAct} variant="contained" color={dlgAction === 'APPROVE' ? 'primary' : 'error'} disabled={acting}>
            {acting ? 'Working…' : (dlgAction === 'APPROVE' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// Original page component (for route usage)
function ApprovalsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dlgAction, setDlgAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [dlgNote, setDlgNote] = useState('');
  const [actingId, setActingId] = useState<number | string | null>(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await approvalsApi.listMy({
        status: 'PENDING',
        currentStepOnly: true,
        limit: 100,
      });

      const normalized: Row[] = (data.items as ApprovalListItem[]).map((it) => {
        const task = it.task;
        const submittedAt =
          (task && (task as any)?.createdAt) ||
          (task && (task as any)?.startDate) ||
          it.actedAt ||
          new Date().toISOString();

        const href = getEntityRoute(task);

        return {
          id: task?.id ?? it.taskId ?? it.id,
          taskId: Number(task?.id ?? it.taskId ?? it.id),
          entity: String(task?.type || 'TASK'),
          title: task?.title || '(untitled task)',
          submittedByText: submittedByToText(task?.createdBy ?? null),
          submittedAt: String(submittedAt),
          href,
          step: task?.currentApprovalStep ?? undefined,
        };
      });

      setRows(normalized);
    } catch (e: any) {
      setErr(parseErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.title?.toLowerCase().includes(q) ||
      r.entity?.toLowerCase().includes(q) ||
      r.submittedByText.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const openAct = (taskIdLike: number | string, action: 'APPROVE' | 'REJECT') => {
    setActingId(taskIdLike);
    setDlgAction(action);
    setDlgNote('');
    setDlgOpen(true);
  };

  const doAct = async () => {
    if (actingId == null) return;
    const taskId = Number(actingId);
    if (!Number.isFinite(taskId) || taskId <= 0) {
      setErr('Invalid task id.');
      return;
    }

    setActing(true);
    setErr(null);
    try {
      if (dlgAction === 'APPROVE') {
        await approvalsApi.approve(taskId, dlgNote || undefined);
      } else {
        await approvalsApi.reject(taskId, dlgNote || undefined);
      }
      setDlgOpen(false);
      setActingId(null);
      await load();
    } catch (e) {
      setErr(parseErr(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <Box sx={{ p: 3, minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Enhanced Header Section */}
      <Card 
        elevation={0} 
        sx={{ 
          mb: 3, 
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                }}
              >
                <AssignmentIcon />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>
                  Pending Approvals
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Review and approve pending requests
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={load}
                disabled={loading}
                sx={{ borderRadius: 2 }}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* Search and Filter Section */}
      <Card elevation={0} sx={{ mb: 3, border: `1px solid ${theme.palette.divider}` }}>
        <CardContent sx={{ p: 2 }}>
          <TextField
            size="small"
            placeholder="Search by title, entity, or submitter…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ 
              width: { xs: "100%", sm: 400 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      size="small"
                      onClick={() => { setSearch(""); setPage(0); }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* Approvals List */}
      <Grid container spacing={2}>
        {loading ? (
          [...Array(3)].map((_, index) => (
            <Grid item xs={12} key={index}>
              <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Skeleton variant="circular" width={48} height={48} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="60%" height={24} />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                    <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : paged.length === 0 ? (
          <Grid item xs={12}>
            <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
              <CardContent sx={{ p: 4, textAlign: "center" }}>
                <AssignmentIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No pending approvals
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {search ? "Try adjusting your search criteria" : "All caught up! No approvals are waiting for your review."}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          paged.map((r) => {
            const entityInfo = getEntityInfo(r.entity);
            const EntityIcon = entityInfo.icon;
            
            return (
              <Grid item xs={12} key={`${r.taskId}-${r.id}`}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    border: `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
                      <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            backgroundColor: alpha(
                              entityInfo.color === 'primary' ? theme.palette.primary.main :
                              entityInfo.color === 'secondary' ? theme.palette.secondary.main :
                              entityInfo.color === 'info' ? theme.palette.info.main :
                              entityInfo.color === 'success' ? theme.palette.success.main :
                              theme.palette.grey[500], 0.1),
                            color: entityInfo.color === 'primary' ? theme.palette.primary.main :
                                   entityInfo.color === 'secondary' ? theme.palette.secondary.main :
                                   entityInfo.color === 'info' ? theme.palette.info.main :
                                   entityInfo.color === 'success' ? theme.palette.success.main :
                                   theme.palette.grey[500],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <EntityIcon />
                        </Box>
                        
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                            <Typography variant="h6" fontWeight={600} noWrap>
                              {r.title}
                            </Typography>
                            <Chip 
                              size="small" 
                              label={entityInfo.label}
                              color={entityInfo.color as any}
                              variant="outlined"
                            />
                          </Stack>
                          
                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <Box display="flex" alignItems="center" gap={1}>
                              <PersonIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {r.submittedByText}
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" color="text.secondary">
                              {fmtDateTime(r.submittedAt)}
                            </Typography>
                            
                            {typeof r.step === 'number' && (
                              <Chip 
                                size="small" 
                                label={`Step ${r.step}`} 
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} alignItems="center">
                        {r.href ? (
                          <Tooltip title="View Details">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LaunchIcon />}
                              onClick={() => {
                                if (r.href) {
                                  navigate(r.href);
                                }
                              }}
                              sx={{ borderRadius: 2 }}
                            >
                              Open
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Entity not found">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled
                              sx={{ borderRadius: 2 }}
                            >
                              No Link
                            </Button>
                          </Tooltip>
                        )}
                        
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => openAct(r.taskId, 'APPROVE')}
                          sx={{ borderRadius: 2 }}
                        >
                          Approve
                        </Button>
                        
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => openAct(r.taskId, 'REJECT')}
                          sx={{ borderRadius: 2 }}
                        >
                          Reject
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Pagination */}
      {filtered.length > 0 && (
        <Card elevation={0} sx={{ mt: 2, border: `1px solid ${theme.palette.divider}` }}>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              }
            }}
          />
        </Card>
      )}

      {/* Approve/Reject dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dlgAction === 'APPROVE' ? 'Approve' : 'Reject'} request</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You can leave a note (optional).
          </Typography>
          <TextField
            label="Note (optional)"
            multiline minRows={3} fullWidth
            value={dlgNote}
            onChange={(e)=>setDlgNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDlgOpen(false)}>Cancel</Button>
          <Button onClick={doAct} variant="contained" color={dlgAction === 'APPROVE' ? 'primary' : 'error'} disabled={acting}>
            {acting ? 'Working…' : (dlgAction === 'APPROVE' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Wrapper component that can be used both as a page and as a dialog
interface ApprovalsProps {
  open?: boolean;
  onClose?: () => void;
}

export default function Approvals({ open, onClose }: ApprovalsProps = {}) {
  // If used as dialog (open prop provided)
  if (open !== undefined && onClose) {
    return <ApprovalsDialog open={open} onClose={onClose} />;
  }

  // If used as page (no open prop), render the original page layout
  return <ApprovalsPage />;
}